import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import './Users.css';
import { formatAppDate } from '../../utils/dateUtils';

const ViewRole = forwardRef(({ roleId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit, onRoleCreated }, ref) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const [role, setRole] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { showSuccess, showError, showWarning } = useMessages();

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    if (roleId && !createMode) {
      fetchRole();
    } else if (createMode) {
      // Initialize with default values for new role
      const newRole = {
        role_code: '',
        role_name: '',
        description: '',
        is_system: false,
        is_active: true
      };
      setRole(newRole);
      setEditData(newRole);
    }
  }, [roleId, createMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          // In view mode, go back to list
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  const fetchRole = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/roles/${roleId}`));
      if (response.ok) {
        const data = await response.json();
        setRole(data);
        setEditData(data);
      } else {
        setError(t('roles.view.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching role:', error);
      setError(t('roles.view.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const isSystemRole = role && role.is_system;

  const handleSave = async () => {
    try {
      // Validate required fields
      if (!editData.role_code || !editData.role_name) {
        showError(t('roles.view.validation.required'));
        return;
      }

      // Prepare data for API
      const saveData = {
        role_code: editData.role_code,
        role_name: editData.role_name,
        description: editData.description,
        is_active: editData.is_active
      };

      const url = createMode
        ? getApiUrl('/api/roles/')
        : getApiUrl(`/api/roles/${roleId}`);

      const method = createMode ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(saveData)
      });

      if (response.ok) {
        const updatedRole = await response.json();
        setRole(updatedRole);
        setEditData(updatedRole);
        setHasChanges(false);

        if (createMode) {
          showSuccess(t('roles.view.messages.createdSuccess'));
          if (onRoleCreated) {
            onRoleCreated(updatedRole.role_id);
          }
        } else {
          showSuccess(t('roles.view.messages.updatedSuccess'));
        }

        // Call onSave callback if provided
        if (onSave) {
          onSave();
        }
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error saving role:', error);
      showError(tCommon('errors.genericTitle'), t('roles.view.error.loadingError'));
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm(t('roles.view.confirmations.unsavedChanges'))) {
        setEditData(role || {});
        setHasChanges(false);
        if (onCancel) onCancel();
      }
    } else {
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('roles.view.confirmations.deleteConfirm', { name: role.role_name }))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/roles/${roleId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('roles.view.messages.deletedSuccess'));
        if (onBack) onBack();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      showError(tCommon('errors.genericTitle'), t('roles.view.error.fetchFailed'));
    }
  };

  if (loading) {
    return <div className="view-container"><div className="loading">{t('roles.loading')}</div></div>;
  }

  if (error && !role) {
    const errorMessage = typeof error === 'string' ? error : 'An error occurred while loading the role';
    return <div className="view-container"><div className="error-message">{errorMessage}</div></div>;
  }

  if (!role && !createMode) {
    return <div className="view-container"><div className="no-data">{t('roles.view.titles.noRoleFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <h2>
            {createMode ? t('roles.view.titles.newRole') : role?.role_name || t('roles.view.titles.viewRole')}
          </h2>
          <span className="header-id">{!createMode && role?.role_code}</span>
        </div>
        <div className="header-status">
          {editData.is_active ? (
            <span className="status-badge status-active">{t('roles.filters.status.active')}</span>
          ) : (
            <span className="status-badge status-inactive">{t('roles.filters.status.inactive')}</span>
          )}
        </div>
      </div>

      {isSystemRole && (editMode || createMode) && (
        <div className="info-message" style={{ padding: '10px 15px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', margin: '0 0 15px 0', color: '#856404' }}>
          {t('roles.view.messages.systemRoleReadonly')}
        </div>
      )}

      <div className="view-content">
        <div className="form-section">
          <h3>{t('roles.view.sections.roleInfo')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('roles.view.fields.code')} *</label>
              <input
                type="text"
                value={editData.role_code || ''}
                onChange={(e) => handleFieldChange('role_code', e.target.value.toUpperCase())}
                disabled={!createMode || isSystemRole}
                required
                maxLength="20"
              />
            </div>
            <div className="form-group">
              <label>{t('roles.view.fields.name')} *</label>
              <input
                type="text"
                value={editData.role_name || ''}
                onChange={(e) => handleFieldChange('role_name', e.target.value)}
                disabled={(!editMode && !createMode) || isSystemRole}
                required
              />
            </div>
            <div className="form-group full-width">
              <label>{t('roles.view.fields.description')}</label>
              <textarea
                value={editData.description || ''}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                disabled={(!editMode && !createMode) || isSystemRole}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>{t('roles.view.fields.status')}</label>
              <select
                value={editData.is_active ? 'active' : 'inactive'}
                onChange={(e) => handleFieldChange('is_active', e.target.value === 'active')}
                disabled={(!editMode && !createMode) || isSystemRole}
              >
                <option value="active">{t('roles.filters.status.active')}</option>
                <option value="inactive">{t('roles.filters.status.inactive')}</option>
              </select>
            </div>
          </div>
        </div>

        {role && role.created_date && (
          <div className="form-section">
            <h3>{t('roles.view.sections.systemInfo')}</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>{t('roles.view.fields.createdDate')}</label>
                <span>{formatAppDate(role.created_date, 'datetime')}</span>
              </div>
              {role.updated_date && (
                <div className="info-item">
                  <label>{t('roles.view.fields.lastUpdated')}</label>
                  <span>{formatAppDate(role.updated_date, 'datetime')}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default ViewRole;
